import { startPPT } from "../../../services/assetsGenerationAPI";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Start PPT: requires pptId (returned from /get_templates_by_llm)
const startPPTGeneration = async (token, selectedSections, pptId) => {
  try {
    const url = `${BASE_URL}/start-ppt?pptId=${pptId}`;
    console.log("[startPPTGeneration] URL:", url);

    const templateIds = (selectedSections || []).flatMap(section =>
      Array.isArray(section.ppt_template_ids) ? section.ppt_template_ids : [section.ppt_template_ids]
    ).filter(Boolean);

    console.log("[startPPTGeneration] Template IDs:", templateIds);

    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(templateIds),
    });

    if (!response.ok) {
      try {
        const errorResponse = await response.json();
        console.error("[startPPTGeneration] Backend error:", errorResponse);
      } catch (_) { }
      return { error: "Something went wrong. Please try again." };
    }
    return await response.json();
  } catch (error) {
    console.error("[startPPTGeneration] Unexpected error:", error);
    return { error: "Something went wrong. Please try again." };
  }
};

// Regenerate PPT: calls /regenerate-ppt with an existing pptId (for failed projects)
const regeneratePPTGeneration = async (token, pptId) => {
  try {
    const url = `${BASE_URL}/regenerate-ppt?pptId=${pptId}`;
    console.log("[regeneratePPTGeneration] URL:", url);

    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    });

    if (!response.ok) {
      try {
        const errorResponse = await response.json();
        console.error("[regeneratePPTGeneration] Backend error:", errorResponse);
      } catch (_) { }
      return { error: "Something went wrong. Please try again." };
    }
    return await response.json();
  } catch (error) {
    console.error("[regeneratePPTGeneration] Unexpected error:", error);
    return { error: "Something went wrong. Please try again." };
  }
};

export const handleStartGeneratingPresentation = async ({
  publicationId,
  token,
  contentLevel,
  slidesCount,
  selectedSections,
  setActiveTasks,
  setTaskStatus,
  setAlert,
  setupWebSocket,
  setOpenPresentationDialog,
  setPptId,
  existingPptId,  // pptId from PresentationDialog step-1
  setRefreshTrigger,
}) => {
  setOpenPresentationDialog(false);

  try {
    const uniqueId = existingPptId || "pending";
    setActiveTasks((prev) => {
      const nextArr = [...(prev.ppt || [])];
      if (!nextArr.includes(uniqueId)) nextArr.push(uniqueId);
      return { ...prev, ppt: nextArr };
    });
    localStorage.setItem("activeTasks", "true");
    setTaskStatus(prev => ({
      ...prev,
      ppt: {
        ...(prev.ppt || {}),
        [uniqueId]: { progress: 0, status: "queued", publicationId: Number(publicationId), pptId: existingPptId || null }
      }
    }));

    console.log("[handleStartGeneratingPresentation] pptId:", existingPptId, "sections:", selectedSections);

    const result = await startPPTGeneration(token, selectedSections, existingPptId);
    console.log("[handleStartGeneratingPresentation] API Result:", result);

    if (result.error) {
      setAlert({ severity: "warning", message: result.error });
      if (!existingPptId) {
        setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== "pending") }));
      } else {
        setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== existingPptId) }));
        setTaskStatus(prev => ({
          ...prev,
          ppt: {
            ...(prev.ppt || {}),
            [existingPptId]: { ...(prev.ppt?.[existingPptId] || {}), progress: 0, status: "error" }
          }
        }));
      }
    } else if (result.task_id) {
      const finalPptId = result.pptId || existingPptId;
      if (finalPptId) {
        console.log("[handleStartGeneratingPresentation] Storing pptId:", finalPptId);
        setPptId(finalPptId);
      }
      if (!existingPptId) {
        setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== "pending") }));
      }
      setupWebSocket(result.task_id, "ppt", publicationId, finalPptId, (message, severity) => {
        setAlert({ severity, message });
      });
      setAlert({ severity: "success", message: "Presentation Generation Started" });
      if (setRefreshTrigger) {
        setRefreshTrigger(prev => prev + 1);
      }
    } else {
      console.log("[handleStartGeneratingPresentation] No task_id in response:", result);
      setAlert({ severity: "warning", message: "Unexpected response from server." });
      if (!existingPptId) {
        setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== "pending") }));
      } else {
        setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== existingPptId) }));
        setTaskStatus(prev => ({
          ...prev,
          ppt: {
            ...(prev.ppt || {}),
            [existingPptId]: { ...(prev.ppt?.[existingPptId] || {}), progress: 0, status: "error" }
          }
        }));
      }
    }
  } catch (error) {
    console.error("[handleStartGeneratingPresentation] Error:", error);
    setAlert({ severity: "warning", message: "Failed to start PPT generation. Please try again." });
    if (!existingPptId) {
      setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== "pending") }));
    } else {
      setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== existingPptId) }));
      setTaskStatus(prev => ({
        ...prev,
        ppt: {
          ...(prev.ppt || {}),
          [existingPptId]: { ...(prev.ppt?.[existingPptId] || {}), progress: 0, status: "error" }
        }
      }));
    }
  }
};

export const handleRegeneratePresentation = async ({
  pptId,
  publicationId,
  token,
  setActiveTasks,
  setTaskStatus,
  setAlert,
  setupWebSocket,
  setRefreshTrigger,
}) => {
  try {
    const uniqueId = pptId || "pending";
    setActiveTasks((prev) => {
      const nextArr = [...(prev.ppt || [])];
      if (!nextArr.includes(uniqueId)) nextArr.push(uniqueId);
      return { ...prev, ppt: nextArr };
    });
    localStorage.setItem("activeTasks", "true");
    setTaskStatus(prev => ({
      ...prev,
      ppt: {
        ...(prev.ppt || {}),
        [uniqueId]: { progress: 0, status: "queued", publicationId: Number(publicationId), pptId }
      }
    }));

    console.log("[handleRegeneratePresentation] Regenerating pptId:", pptId);

    const result = await regeneratePPTGeneration(token, pptId);
    console.log("[handleRegeneratePresentation] API Result:", result);

    if (result.error) {
      setAlert({ severity: "warning", message: result.error });
      setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== pptId) }));
      setTaskStatus(prev => ({
        ...prev,
        ppt: {
          ...(prev.ppt || {}),
          [pptId]: { ...(prev.ppt?.[pptId] || {}), progress: 0, status: "error" }
        }
      }));
    } else if (result.task_id) {
      setupWebSocket(result.task_id, "ppt", publicationId, pptId, (message, severity) => {
        setAlert({ severity, message });
      });
      setAlert({ severity: "success", message: "PPT Regeneration Started" });
      if (setRefreshTrigger) {
        setRefreshTrigger(prev => prev + 1);
      }
    } else {
      console.log("[handleRegeneratePresentation] Unexpected response:", result);
      setAlert({ severity: "warning", message: "Unexpected response from server." });
      setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== pptId) }));
      setTaskStatus(prev => ({
        ...prev,
        ppt: {
          ...(prev.ppt || {}),
          [pptId]: { ...(prev.ppt?.[pptId] || {}), progress: 0, status: "error" }
        }
      }));
    }
  } catch (error) {
    console.error("[handleRegeneratePresentation] Error:", error);
    setAlert({ severity: "warning", message: "Failed to regenerate PPT. Please try again." });
    setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== pptId) }));
    setTaskStatus(prev => ({
      ...prev,
      ppt: {
        ...(prev.ppt || {}),
        [pptId]: { ...(prev.ppt?.[pptId] || {}), progress: 0, status: "error" }
      }
    }));
  }
};


export const handleStartGeneratingPresentationOld = async ({
  publicationId,
  token,
  contentLevel,
  setActiveTasks,
  setTaskStatus,
  setAlert,
  setupWebSocket,
  setOpenPresentationDialog,
}) => {
  const uniqueId = "pending";
  try {
    setActiveTasks((prev) => {
      const nextArr = [...(prev.ppt || [])];
      if (!nextArr.includes(uniqueId)) nextArr.push(uniqueId);
      return { ...prev, ppt: nextArr };
    });
    localStorage.setItem("activeTasks", "true");
    setTaskStatus(prev => ({
      ...prev,
      ppt: {
        ...(prev.ppt || {}),
        [uniqueId]: { progress: 0, status: "queued" }
      }
    }));

    const result = await startPPT(publicationId, token, contentLevel);

    if (result.error) {
      setAlert({ severity: "warning", message: result.error });
      setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== uniqueId) }));
      setTaskStatus(prev => { const n = { ...prev }; if (n.ppt) { n.ppt = { ...n.ppt }; delete n.ppt[uniqueId]; } return n; });
    } else if (result.task_id) {
      setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== uniqueId) }));
      setTaskStatus(prev => { const n = { ...prev }; if (n.ppt) { n.ppt = { ...n.ppt }; delete n.ppt[uniqueId]; } return n; });
      setupWebSocket(result.task_id, "ppt", publicationId, result.project_id || null, (message, severity) => {
        setAlert({ severity, message });
      });
      setAlert({
        severity: "success",
        message: "Abstract PPT Generation Started",
      });
      setOpenPresentationDialog(false);
    }
  } catch (error) {
    setAlert({
      severity: "warning",
      message: "Failed to start PPT generation. Please try again.",
    });
    setActiveTasks(prev => ({ ...prev, ppt: (prev.ppt || []).filter(id => id !== uniqueId) }));
    setTaskStatus(prev => { const n = { ...prev }; if (n.ppt) { n.ppt = { ...n.ppt }; delete n.ppt[uniqueId]; } return n; });
  }
  setOpenPresentationDialog(false);
};

export const handleOpenPresentationDialog = (setOpenPresentationDialog) => {
  setOpenPresentationDialog(true);
};

export const handleClosePresentationDialog = (setOpenPresentationDialog) => {
  setOpenPresentationDialog(false);
};
